import type { SpecialOfferResponse } from 'api/generated/types.gen';

/**
 * True when the offer is gated at all. With no gate anyone can claim it; with a
 * gate, `codeAccess` and `webUserAccess` say which ways in the offer accepts.
 */
export const requiresSpecialOfferAccess = (offer: Pick<SpecialOfferResponse, 'usePassword'>) => !!offer.usePassword;

/** True when a code is one of the ways to claim the offer. */
export const specialOfferAcceptsCode = (offer: Pick<SpecialOfferResponse, 'usePassword' | 'codeAccess'>) => requiresSpecialOfferAccess(offer) && !!offer.codeAccess;

/**
 * True when signing in is one of the ways to claim the offer. The offer is
 * mapped to specific accounts, so signing in does not guarantee access —
 * `noAccess` answers that once the customer is signed in.
 */
export const specialOfferAcceptsSignIn = (offer: Pick<SpecialOfferResponse, 'usePassword' | 'webUserAccess'>) => requiresSpecialOfferAccess(offer) && !!offer.webUserAccess;

/**
 * True when a signed-in customer's account is not included in the offer. They
 * can still get in with a code, if the offer accepts one.
 */
export const isSignedInWithoutOfferAccess = (offer: Pick<SpecialOfferResponse, 'noAccess'>, isSignedIn: boolean) => !!offer.noAccess && isSignedIn;

/**
 * A signed-in customer without access is only locked out when there is nothing
 * left to try — an offer that also accepts a code stays claimable with one.
 */
export const isSpecialOfferAccessDenied = (offer: Pick<SpecialOfferResponse, 'noAccess' | 'usePassword' | 'codeAccess'>, isSignedIn: boolean) => isSignedInWithoutOfferAccess(offer, isSignedIn) && !specialOfferAcceptsCode(offer);

/**
 * Query flag the offer page adds to its own sign-in `returnUrl`, so that on the
 * way back it can tell an intentional sign-in attempt from a plain visit by an
 * already signed-in customer.
 */
export const fromSignInParam = 'fromSignIn';
export const withFromSignInParam = (path: string) => `${path}${path.includes('?') ? '&' : '?'}${fromSignInParam}=1`;